import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

// Admin contact-inbox state + operations, extracted from MessagesInbox so the component is presentational.
export function useMessages(pin) {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/messages", { headers: { "X-Admin-Pin": pin } });
      setMessages(data.messages || []);
      setUnread(data.unread || 0);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => { load(); }, [load]);

  const setRead = async (m, read) => {
    try {
      await api.post(`/admin/messages/${m.id}/read`, { read }, { headers: { "X-Admin-Pin": pin } });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, read } : x)));
      setUnread((u) => Math.max(0, u + (read ? -1 : 1)));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (e, m) => {
    e.stopPropagation();
    try {
      await api.delete(`/admin/messages/${m.id}`, { headers: { "X-Admin-Pin": pin } });
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      if (!m.read) setUnread((u) => Math.max(0, u - 1));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleLead = async (e, m) => {
    e.stopPropagation();
    const next = !m.lead;
    try {
      await api.post(`/admin/messages/${m.id}/lead`, { lead: next }, { headers: { "X-Admin-Pin": pin } });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, lead: next } : x)));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleOpen = (m) => {
    setOpenId((cur) => (cur === m.id ? null : m.id));
    if (!m.read) setRead(m, true);
  };

  const exportCsv = async () => {
    const scope = filter === "leads" ? "leads" : "all";
    try {
      const resp = await api.get("/admin/messages/export", {
        params: { scope },
        headers: { "X-Admin-Pin": pin },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = scope === "leads" ? "videoileo-leads.csv" : "videoileo-messages.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const leadsCount = messages.filter((m) => m.lead).length;
  const unreadCount = messages.filter((m) => !m.read).length;
  const visible = messages.filter((m) =>
    filter === "unread" ? !m.read : filter === "leads" ? m.lead : true
  );
  const tabs = [
    { key: "all", label: "All", count: messages.length },
    { key: "unread", label: "Unread", count: unreadCount },
    { key: "leads", label: "Leads", count: leadsCount },
  ];

  return {
    messages, unread, loading, openId, filter, setFilter,
    setRead, remove, toggleLead, toggleOpen, exportCsv,
    visible, tabs,
  };
}
